import { type MessageDescriptor, i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';

import type { CanaryRecommendation } from '@/api/aiVisibility';
import { Badge } from '@/components/typography';
import { cn } from '@/lib/utils';

type Props = {
  recommendation: CanaryRecommendation;
  className?: string;
};

export const CATEGORY_COLORS: Record<string, string> = {
  brand: 'bg-blue-600 text-white',
  'unbranded discovery': 'bg-blue-600 text-white',
  'product areas': 'bg-blue-400 text-white',
  'head to head': 'bg-aqua-600 text-white',
  sentiment: 'bg-blue-100 text-gray-900',
  'product share of voice': 'bg-blue-100 text-gray-900',
  'competitive citation': 'bg-aqua-600 text-white',
  'competitive visibility': 'bg-aqua-600 text-white',
  'content performance gap': 'bg-aqua-600 text-white',
  'schema/structural': 'bg-aqua-400 text-gray-900',
  'crawler access gap': 'bg-aqua-400 text-gray-900',
  '3rd party authority': 'bg-aqua-200 text-gray-900',
  'schema structural gap': 'bg-aqua-200 text-gray-900',
};

const CATEGORY_LABELS: Record<string, MessageDescriptor> = {
  brand: msg`Brand`,
  'unbranded discovery': msg`Unbranded Discovery`,
  'product areas': msg`Product Areas`,
  'head to head': msg`Head to Head`,
  sentiment: msg`Sentiment`,
  'product share of voice': msg`Product Share of Voice`,
  'competitive citation': msg`Competitive Citation`,
  'competitive visibility': msg`Competitive Visibility`,
  'content performance gap': msg`Content Performance Gap`,
  'schema/structural': msg`Schema/Structural`,
  'crawler access gap': msg`Crawler Access Gap`,
  '3rd party authority': msg`3rd Party Authority`,
  'schema structural gap': msg`Schema Structural Gap`,
};

const CATEGORY_ALIASES: Record<string, string> = {
  // eslint-disable-next-line lingui/no-unlocalized-strings -- API identifiers, not visible copy
  'product area': 'product areas',
};

export const normalizeRecommendationCategory = (value: string) => {
  const normalized = value.replaceAll('_', ' ').replaceAll('-', ' ').toLowerCase();
  return CATEGORY_ALIASES[normalized] ?? normalized;
};

export const getRecommendationCategoryColor = (value: string) =>
  CATEGORY_COLORS[normalizeRecommendationCategory(value)] ?? 'bg-gray-700 text-white';

export const getRecommendationCategoryLabel = (value: string) => {
  const normalized = normalizeRecommendationCategory(value);
  const label = CATEGORY_LABELS[normalized];
  if (label) return i18n._(label);
  return value
    .replaceAll('_', ' ')
    .split(' ')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};

const RecommendationTags = ({ recommendation, className }: Props) => {
  const categories = [recommendation.gapArea, recommendation.scopeType]
    .filter((value): value is string => !!value)
    .filter(
      (value, index, values) =>
        values.findIndex(
          (item) =>
            normalizeRecommendationCategory(item) === normalizeRecommendationCategory(value),
        ) === index,
    );
  const impact = recommendation.impact?.toLowerCase();
  const impactLabel =
    impact === 'high'
      ? t`High Impact`
      : impact === 'medium'
        ? t`Medium Impact`
        : impact === 'low'
          ? t`Low Impact`
          : recommendation.impact;
  const impactColor =
    impact === 'high'
      ? 'bg-red-500 text-white'
      : impact === 'medium'
        ? 'bg-yellow-400 text-gray-900'
        : 'bg-green-400 text-gray-900';

  return (
    <span className={cn('flex flex-wrap gap-2', className)}>
      {categories.map((category) => (
        <Badge
          key={category}
          color="default"
          variant="solid"
          size="small"
          showTooltip={false}
          className={getRecommendationCategoryColor(category)}
        >
          {getRecommendationCategoryLabel(category)}
        </Badge>
      ))}
      {impactLabel && (
        <Badge
          color="default"
          variant="solid"
          size="small"
          showTooltip={false}
          className={impactColor}
        >
          {impactLabel}
        </Badge>
      )}
    </span>
  );
};

export default RecommendationTags;
